import mo from 'moment';
import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

// Components
import LeftArrow from '@material-ui/icons/KeyboardArrowLeftRounded';
import Grid from '@material-ui/core/Grid';
import HelpIcon from '@material-ui/icons/Help';
import Link from '@material-ui/core/Link';

import ContentBox from 'components/CncUI/ContentBox';
import TextField from 'components/CncUI/TextField';
import TextFieldSsn from 'components/CncUI/TextField/TextFieldSsn';
import TextFieldSin from 'components/CncUI/TextField/TextFieldSin';
import TextFieldTaxId from 'components/CncUI/TextField/TextFieldTaxId';
import TextFieldEmail from 'components/CncUI/TextField/TextFieldEmail';
import Typography from 'components/CncUI/Typography';
import Radio from 'components/CncUI/Radio';
import Button from 'components/CncUI/Button';
import Tooltip from 'components/CncUI/Tooltip';
import Checkbox from 'components/CncUI/Checkbox';
import Alert from 'components/CncUI/Alert';

import { formatDatePart, findMaxDay } from 'utilities/dateHelp';
import validateAgainstMask from 'utilities/validateAgainstMask';
import SupportFooter from 'components/SupportFooter';

// Constants
import { dateMask } from 'constants/masks';
import valid from 'utilities/valid';
import emailBlacklist from 'constants/registrationEmailDomainBlacklist';
import useCheckAuthentication from 'hooks/useCheckAuthentication';
import useSnackbar from 'hooks/useSnackbar';
import useFeatureFlags from 'hooks/useFeatureFlags';

const NAME_RULES = /^[a-zA-Z \-.']*$/;
const NUMBERS_ONLY = /^[0-9]*$/;

const FLAG_MINORS = 'MyCnC-Minors';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  box: {
    margin: '40px auto',
    [breakpoints.up('md')]: {
      padding: '40px 69px',
      width: 474,
    },
    '& h5': {
      marginTop: 0,
    },
    [breakpoints.down('sm')]: {
      width: 'auto',
      padding: '40px 20px',
    },
  },
  helpIcon: {
    marginTop: 10,
  },
  gridItems: {
    marginRight: 2,
    marginBottom: 12,
    width: '100%',
  },
  toolTip: {
    display: 'flex',
    marginTop: 10,
  },
  nextButton: {
    marginTop: 36,
    alignItems: 'center',
    width: '100% !important',
  },
  backButton: {
    marginTop: 24,
    width: '100% !important',
  },
  emailLocked: {
    display: 'none',
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: palette.cncUI.default.background,
    marginRight: 8,
  },
  group: {
    flexDirection: 'row',
  },
  sample: {
    color: palette.cncUI.primary.gray['+2'],
  },
  future: {
    color: palette.cncUI.alert.red.primary,
    width: 300,
    marginTop: -8,
  },
  link: {
    color: palette.cncUI.misc.link,
    textDecoration: 'none',
  },
}));

const FindAccount = props => {
  const classes = useStyles();
  const notify = useSnackbar();
  const { isEnabled } = useFeatureFlags();
  const { setStep, changeState, state, isMobile, canadaEnabled } = props;
  const { lockedEmail } = state;
  const [maxDay, setMaxDay] = useState(31);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    ssn: '',
    sin: '',
    dob: '',
    month: '',
    year: '',
    day: '',
    guardianFirstName: '',
    guardianLastName: '',
  });
  const [emailValid, setEmailValid] = useState(true);
  const [emailMatches, setEmailMatches] = useState(true);
  const [errMessage, setErrMessage] = useState('');
  const emailLocked = !!lockedEmail;
  const [email, setEmail] = useState(lockedEmail || '');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [pasteDisabled, setPasteDisabled] = useState(false);
  const [idType, setIdType] = useState('ssn');
  const [isFutureDob, setIsFutureDob] = useState(false);
  const [isMinor, setIsMinor] = useState(false);
  const [readAccept, setReadAccept] = useState(false);
  const minorsEnabled = isEnabled(FLAG_MINORS);

  const maxYear = new Date().getFullYear();
  const minYear = maxYear - 105;

  useEffect(() => {
    if (formData?.year?.length === 4) {
      const birthMonth = formatDatePart(formData.month);
      const birthDay = formatDatePart(formData.day);
      const birthYear = formData.year;
      setIsFutureDob(false);
      const dob = `${birthMonth}/${birthDay}/${birthYear}`;
      setFormData({ ...formData, dob });
      setIsFutureDob(new Date(dob) > new Date());
      setMaxDay(findMaxDay(birthMonth, birthYear) || 31);
      if (minorsEnabled && formData.month && formData.day) {
        setIsMinor(mo().diff(new Date(dob), 'years') < 18);
      }
    }
  }, [formData.year, formData.month, formData.day]);

  useCheckAuthentication(token => {
    if (token) {
      const { email } = token.claims || {};
      changeState({ lockedEmail: email });
      setEmail(email);
      setFormData({ ...formData, email: email });
    }
  });

  const nextDisabled = !(
    formData.firstName &&
    formData.lastName &&
    formData.month > 0 &&
    formData.month < 13 &&
    formData.day > 0 &&
    formData.day <= maxDay &&
    formData.year > minYear &&
    formData.year <= maxYear &&
    new Date(formData.dob) < new Date() &&
    (!isMinor || formData.guardianFirstName) &&
    (!isMinor || formData.guardianLastName) &&
    validateAgainstMask(dateMask, formData.dob || '') &&
    ((!isMobile && idType === 'ssn' && formData?.ssn?.length === 11) ||
      (!isMobile && idType === 'sin' && formData?.sin?.length === 11) ||
      (isMobile && idType === 'ssn' && formData?.ssn.length === 9) ||
      (isMobile && idType === 'sin' && formData?.sin.length === 9)) &&
    emailValid &&
    ((email !== '' && email === emailConfirm) || emailLocked) &&
    readAccept
  );

  const onNext = () => {
    idType === 'sin'
      ? (formData.sin =
          formData.sin.length === 9
            ? formData.sin.substring(0, 3) +
              '-' +
              formData.sin.substring(3, 6) +
              '-' +
              formData.sin.substring(
                formData.sin.length - 3,
                formData.sin.length,
              )
            : formData.sin || null)
      : (formData.ssn =
          formData.ssn.length === 9
            ? formData.ssn.substring(0, 3) +
              '-' +
              formData.ssn.substring(3, 5) +
              '-' +
              formData.ssn.substring(
                formData.ssn.length - 4,
                formData.ssn.length,
              )
            : formData.ssn || null);
    changeState({ form: formData, email, idType, isMinor });
    if (isMinor) {
      setStep('Affidavit');
    } else {
      setStep('SearchAccount');
    }
  };

  const notifyError = type => {
    notify({
      type: 'error',
      message: `The ${type} you provided is not valid. Please update your ${type} to proceed.`,
    });
  };

  const validateForm = () => {
    const id = idType === 'sin' ? formData.sin : formData.ssn;
    if (valid[idType](id)) {
      onNext();
    } else {
      notifyError(idType.toUpperCase());
    }
  };

  const checkEmailValidation = () => {
    if (!valid.email(email)) setEmailValid(false);
    setErrMessage('Please enter a valid email');
    if (
      emailBlacklist.some(blackListedEmail => email.endsWith(blackListedEmail))
    ) {
      setEmailValid(false);
      setErrMessage(
        'The email domain entered can not be used for registration.',
      );
    }
    checkEmailConfirm();
  };

  const checkEmailConfirm = () => {
    setEmailMatches(email === emailConfirm || emailConfirm === '');
  };

  const emailOnChange = e => {
    const { target = {} } = e;
    const { value } = target;
    setFormData({ ...formData, email: e.target.value });
    setEmail(value);
    if (!emailValid) setEmailValid(valid.email(value));
  };

  const emailLabel = () =>
    emailValid ? (isMinor ? 'Guardian Email' : 'Email') : errMessage;
  const emailConfirmLabel = () =>
    emailMatches || (email && email === emailConfirm)
      ? isMinor
        ? 'Confirm Guardian Email'
        : 'Confirm Email'
      : 'Must match Email';
  const disclaimer = (
    <Typography variant="caption">
      You accept this{' '}
      <Link
        href="https://www.castandcrew.com/privacy-policy/"
        target="_blank"
        rel="noopener noreferrer"
        id="FindAccount-PrivacyPolicyLink"
        className={classes.link}
      >
        Privacy Policy
      </Link>
      , including verification of your identity with third parties, when you
      sign up for access, or use our products, services, content, features,
      technologies or functions offered on our website and all related sites,
      applications, and services.
      <br />
      <br />
      You authorize your wireless carrier to use or disclose information about
      your account and your wireless device, if available, to Cast & Crew or its
      service provider for the duration of your business relationship, solely to
      help them identify you or your wireless device and to prevent fraud. See
      our Privacy Policy for how to see how we treat your data.
    </Typography>
  );

  return (
    <>
      <ContentBox className={classes.box} id="FindAccount">
        <Grid container spacing={3} direction="column" alignItems="center">
          <Grid item xs={12} sm={8} md={12}>
            <Typography variant="h3" center>
              {minorsEnabled
                ? "Let's find your/your minor's account"
                : "Let's find your account"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={12} style={{ marginBottom: 28 }}>
            <Typography variant="body2" align="center">
              We'll use this information to find you in our database.
              <br />
              Please use your full, legal name.
              <br />
              <br />* Denotes required field
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={12}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  value={formData.firstName}
                  onChange={e => {
                    if (NAME_RULES.test(e.target.value)) {
                      setFormData({ ...formData, firstName: e.target.value });
                    }
                  }}
                  required
                  className={classes.gridItems}
                  label="First Name"
                  id="FindAccountFirstName"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={formData.lastName}
                  onChange={e => {
                    if (NAME_RULES.test(e.target.value)) {
                      setFormData({ ...formData, lastName: e.target.value });
                    }
                  }}
                  required
                  className={classes.gridItems}
                  label="Last Name"
                  id="FindAccountLastName"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography size="long" noMargin>
                  Select the identification number type below that you use for
                  taxes and payroll. *
                </Typography>
                <Radio.Group
                  aria-label="Identification Number"
                  name="identification number"
                  id="FindAccountRadioGroupId"
                  className={classes.group}
                  value={idType}
                  onChange={e => {
                    setIdType(e.target.value);
                    setFormData({ ...formData, ssn: '', sin: '' });
                    setReadAccept(false);
                  }}
                >
                  <Radio
                    value="ssn"
                    id="FindAccountRadioSsn"
                    labelPosition="right"
                  >
                    American SSN
                  </Radio>
                  <Radio
                    value="sin"
                    id="FindAccountRadioSin"
                    labelPosition="right"
                  >
                    Canadian SIN
                  </Radio>
                </Radio.Group>
              </Grid>
              <Grid item xs={11}>
                {!isMobile && idType === 'ssn' ? (
                  <TextFieldSsn
                    onChange={e => {
                      setFormData({ ...formData, ssn: e.target.value });
                    }}
                    value={formData.ssn}
                    className={classes.gridItems}
                    toggle
                    required
                    label="Social Security Number"
                    id="FindAccountSsn"
                    inputProps={{
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                    }}
                  />
                ) : !isMobile && idType === 'sin' ? (
                  <TextFieldSin
                    onChange={e => {
                      setFormData({ ...formData, sin: e.target.value });
                    }}
                    value={formData.sin}
                    className={classes.gridItems}
                    toggle
                    required
                    label="Social Insurance Number"
                    id="FindAccountSin"
                    inputProps={{
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                    }}
                  />
                ) : (
                  <TextFieldTaxId
                    onChange={e => {
                      if (NUMBERS_ONLY.test(e.target.value)) {
                        setFormData(
                          idType === 'ssn'
                            ? { ...formData, ssn: e.target.value }
                            : { ...formData, sin: e.target.value },
                        );
                      }
                    }}
                    value={idType === 'ssn' ? formData.ssn : formData.sin}
                    className={classes.gridItems}
                    required
                    label={
                      idType === 'ssn'
                        ? 'Social Security Number'
                        : 'Social Insurance Number'
                    }
                    id={idType === 'ssn' ? 'FindAccountSsn' : 'FindAccountSin'}
                    inputProps={{
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                      maxLength: 9,
                    }}
                  />
                )}
                <Typography>Date of Birth *</Typography>
                <Typography className={classes.sample} italic>
                  example: 04 28 1984
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <div className={classes.toolTip}>
                  <Tooltip
                    id="FindAccountToolTip"
                    enterTouchDelay={100}
                    position={isMobile ? 'top-right' : 'right-center'}
                    size={isMobile ? 'small' : 'large'}
                    content={
                      canadaEnabled
                        ? `We require your SSN or SIN to verify your identity. If you are a dual national, you will need to register with two separate emails, one for each ID number.`
                        : `We require your Social Security Number to verify your identity.
              We use 128-bit encryption to keep your data safe and will never sell
              your data to any third-parties.`
                    }
                  >
                    <HelpIcon className={classes.helpIcon} />
                  </Tooltip>
                </div>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      value={formData.month}
                      onChange={e => {
                        if (NUMBERS_ONLY.test(e.target.value)) {
                          setFormData({ ...formData, month: e.target.value });
                        }
                      }}
                      error={
                        parseInt(formData.month) > 12 ||
                        parseInt(formData.month) < 1 ||
                        isFutureDob
                      }
                      helperText={
                        parseInt(formData.month) > 12 ||
                        parseInt(formData.month) < 1
                          ? 'Invalid month'
                          : ''
                      }
                      required
                      className={classes.gridItems}
                      label="Month"
                      id="FindAccount-DobMonth"
                      inputProps={{
                        maxLength: 2,
                        pattern: '[0-9]*',
                        inputMode: 'numeric',
                      }}
                    />
                    {isFutureDob && (
                      <Typography variant="caption" className={classes.future}>
                        Date of Birth can't be in the future.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      value={formData.day}
                      onChange={e => {
                        if (NUMBERS_ONLY.test(e.target.value)) {
                          setFormData({ ...formData, day: e.target.value });
                        }
                      }}
                      error={
                        parseInt(formData.day) > maxDay ||
                        parseInt(formData.day) < 1 ||
                        isFutureDob
                      }
                      helperText={
                        parseInt(formData.day) > maxDay ||
                        parseInt(formData.day) < 1
                          ? 'Invalid day'
                          : ''
                      }
                      required
                      className={classes.gridItems}
                      label="Day"
                      id="FindAccount-DobDay"
                      inputProps={{
                        maxLength: 2,
                        pattern: '[0-9]*',
                        inputMode: 'numeric',
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={5}>
                    <TextField
                      value={formData.year}
                      onChange={e => {
                        if (NUMBERS_ONLY.test(e.target.value)) {
                          setFormData({ ...formData, year: e.target.value });
                        }
                      }}
                      warning={isMinor}
                      error={
                        parseInt(formData.year) < minYear ||
                        parseInt(formData.year) > maxYear ||
                        isFutureDob
                      }
                      helperText={
                        (parseInt(formData.year) < minYear ||
                          parseInt(formData.year) > maxYear) &&
                        !isFutureDob
                          ? 'Invalid year'
                          : ''
                      }
                      required
                      className={classes.gridItems}
                      label="Year"
                      id="FindAccount-DobYear"
                      inputProps={{
                        maxLength: 4,
                        pattern: '[0-9]*',
                        inputMode: 'numeric',
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {isMinor && (
                <Grid item xs={12}>
                  <Alert warning title="Crew Member is a Minor">
                    Minor (under 18 years old) will not be able to provide an
                    email address, create a digital profile, or directly access
                    any of the Cast & Crew digital products. Their profile must
                    be linked to a guardian who can access the MyCast&Crew
                    profile on their behalf.
                  </Alert>
                </Grid>
              )}
              {isMinor ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h5" noMargin style={{ marginTop: 16 }}>
                      Guardian Account Email
                    </Typography>
                    <Typography
                      size="short"
                      noMargin
                      style={{ marginBottom: 20 }}
                    >
                      We’ll send an activation email to this address. It’s also
                      your username.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={formData.guardianFirstName}
                      onChange={e => {
                        if (NAME_RULES.test(e.target.value)) {
                          setFormData({
                            ...formData,
                            guardianFirstName: e.target.value,
                          });
                        }
                      }}
                      required
                      className={classes.gridItems}
                      label="Guardian First Name"
                      id="FindAccountGuardianFirstName"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={formData.guardianLastName}
                      onChange={e => {
                        if (NAME_RULES.test(e.target.value)) {
                          setFormData({
                            ...formData,
                            guardianLastName: e.target.value,
                          });
                        }
                      }}
                      required
                      className={classes.gridItems}
                      label="Guardian Last Name"
                      id="FindAccountGuardianLastName"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldEmail
                      value={email}
                      onChange={emailOnChange}
                      error={!emailValid}
                      onBlur={checkEmailValidation}
                      id="FindAccountGuardianEmail"
                      required
                      label={emailLabel()}
                      disabled={!!emailLocked}
                      className={classes.gridItems}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldEmail
                      value={emailConfirm}
                      error={
                        !emailMatches || !!(email && email !== emailConfirm)
                      }
                      onBlur={checkEmailConfirm}
                      onChange={
                        pasteDisabled
                          ? () => setPasteDisabled(false)
                          : e => setEmailConfirm(e.target.value)
                      }
                      onPaste={() => setPasteDisabled(true)}
                      className={
                        !emailLocked ? classes.gridItems : classes.emailLocked
                      }
                      id="FindAccountGuardianConfirmEmail"
                      required
                      label={emailConfirmLabel()}
                      disabled={emailLocked}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h5" noMargin style={{ marginTop: 16 }}>
                      MyCast&Crew Account Email
                    </Typography>
                    <Typography
                      size="short"
                      noMargin
                      style={{ marginBottom: 20 }}
                    >
                      We’ll send an activation email to this address. It’s also
                      your username.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldEmail
                      value={email}
                      onChange={emailOnChange}
                      error={!emailValid}
                      onBlur={checkEmailValidation}
                      id="FindAccountEmail"
                      required
                      label={emailLabel()}
                      disabled={!!emailLocked}
                      className={classes.gridItems}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldEmail
                      value={emailConfirm}
                      error={
                        !emailMatches || !!(email && email !== emailConfirm)
                      }
                      onBlur={checkEmailConfirm}
                      onChange={
                        pasteDisabled
                          ? () => setPasteDisabled(false)
                          : e => setEmailConfirm(e.target.value)
                      }
                      onPaste={() => setPasteDisabled(true)}
                      className={
                        !emailLocked ? classes.gridItems : classes.emailLocked
                      }
                      id="FindAccountConfirmEmail"
                      required
                      label={emailConfirmLabel()}
                      disabled={emailLocked}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                {disclaimer}
                <Checkbox
                  id="FindAccountReadAcceptCheckbox"
                  checked={readAccept}
                  onChange={e => setReadAccept(e.target.checked)}
                >
                  I have read this agreement and accept
                </Checkbox>
              </Grid>
              <Grid item xs={12}>
                <Button
                  id="FindAccountNextButton"
                  disabled={nextDisabled}
                  onClick={validateForm}
                  className={classes.nextButton}
                >
                  Next
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  tertiary
                  id="CreatePasswordBackButton"
                  className={classes.backButton}
                  onClick={() => setStep('Welcome')}
                >
                  <LeftArrow className={classes.leftArrow} /> Back
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ContentBox>

      <SupportFooter
        isMobile={isMobile}
        showPrivacy={true}
        canadaEnabled={canadaEnabled}
      />
    </>
  );
};

export default FindAccount;
